import React, { Component } from "react";
import { FormGroup, Label, Input, Button } from "reactstrap";
import Select from "react-select";
import Loader from "react-loaders";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import axiosInstance from "../configuration/Request";
import { ToastContainer } from "react-toastify";
import { showErrorToast, showSuccessToast } from "../Components/Toaster";
import "react-toastify/dist/ReactToastify.css";
import backlogo from "../../assets/images/svg/Back.svg";

const molecularOptions = ["CNV", "mRNA", "Protein", "Mutation", "Fusion", "Methylation", "miRNA"]
  .map((label) => ({ value: label, label }));
const selectStyles = {
  control: (base) => ({ ...base, minHeight: "48px", cursor: "pointer" }),
  option: (base) => ({ ...base, fontSize: "14px", paddingLeft: "30px" }),
  groupHeading: (base) => ({ ...base, fontSize: "14px", color: "#000000" }),
};

export class NewPopulationProjection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      projectionName: "",
      selectedHistologicalSubtype: null,
      cancerTypeOptions: [],
      optionsLoading: true,
      optionsError: "",
      cancerTypeError: "",
      selectedMolecularType: molecularOptions.find((option) => option.value === "miRNA"),
      stageVariables: "TNM",
      file: null,
      fileError: "",
      nameError: "",
      sampleDownloadUrl: "",
      sampleInstructions: "",
      sampleLoading: "",
      submitting: false,
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.fetchFilterOptions();
  }

  fetchFilterOptions = async () => {
    this.optionsController?.abort();
    const controller = new AbortController();
    this.optionsController = controller;
    this.setState({ optionsLoading: true, optionsError: "" });
    const activeSubscription = this.props.loginSubscriptionDetail?.find((item) => item.attributes?.active);
    const plan = activeSubscription?.attributes?.plan?.toLowerCase() || "bronze";
    try {
      const [response, filteredResponse] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/network-dropdowns`, { signal: controller.signal }),
        plan === "bronze"
          ? axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/network-dropdowns/filtered`, { signal: controller.signal })
          : Promise.resolve(null),
      ]);
      if (controller.signal.aborted) return;
      const restricted = filteredResponse?.data?.dropdownData || [];
      const cancerTypeOptions = (response.data?.dropdownData || [])
        .filter((item) => item.cancer_type)
        .map((item) => ({
          label: item.cancer_type,
          options: (item.histological_subtypes || []).filter((subtype) => subtype != null).map((subtype) => ({
            label: subtype,
            value: subtype,
            cancer_type: item.cancer_type,
            isDisabled: restricted.some((entry) => entry.cancer_type === item.cancer_type &&
              entry.histological_subtypes?.includes(subtype)),
          })),
        }));
      this.setState((state) => ({
        cancerTypeOptions,
        selectedHistologicalSubtype: cancerTypeOptions.flatMap((group) => group.options).find((option) =>
          !option.isDisabled && option.value === state.selectedHistologicalSubtype?.value &&
          option.cancer_type === state.selectedHistologicalSubtype?.cancer_type) || null,
        optionsLoading: false,
      }));
    } catch (error) {
      if (!controller.signal.aborted) {
        this.setState({ optionsLoading: false, optionsError: "Unable to load cancer types. Please try again." });
      }
    }
  };

  componentWillUnmount() {
    this.optionsController?.abort();
    this.sampleController?.abort();
    this.submitController?.abort();
  }

  handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    let fileError = "";
    if (!/\.csv$/i.test(file.name)) fileError = "Please choose a CSV file.";
    else if (file.size > 2 * 1024 * 1024) fileError = "The CSV file must be 2 MiB or smaller.";
    else if (!file.size) fileError = "The CSV file is empty. Please choose another file.";
    this.setState({ file: fileError ? null : file, fileError, sampleInstructions: "" });
    if (fileError) event.target.value = "";
  };

  handleSampleFile = async () => {
    if (this.sampleController || this.submitController) return;
    const controller = new AbortController();
    this.sampleController = controller;
    this.setState({ sampleLoading: "download", sampleDownloadUrl: "", sampleInstructions: "" });
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_URL}api/projection-custom/sample_file`,
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      const link = response.data?.data?.url;
      if (!link) throw new Error("The sample download link was not returned.");
      const url = new URL(link, process.env.REACT_APP_BASE_URL);
      if (!["https:", "http:"].includes(url.protocol)) throw new Error("Invalid sample download link.");
      // Load the attachment in a hidden frame: no new tab or CORS-dependent storage request.
      this.setState({
        sampleDownloadUrl: url.href,
        sampleInstructions: "Download requested. Check your browser downloads for the CSV template.",
      });
    } catch (error) {
      if (!controller.signal.aborted) showErrorToast("Unable to load the sample CSV. Please try again.");
    } finally {
      if (!controller.signal.aborted) this.setState({ sampleLoading: "" });
      if (this.sampleController === controller) this.sampleController = null;
    }
  };

  handleCreateAnalysis = async (event) => {
    event.preventDefault();
    if (this.submitController || this.sampleController || this.state.optionsLoading || this.state.optionsError) return;
    const nameError = this.state.projectionName.trim() ? "" : "Please enter a projection name.";
    const fileError = this.state.file ? "" : "Please choose a CSV file.";
    const cancerTypeError = this.state.selectedHistologicalSubtype && !this.state.selectedHistologicalSubtype.isDisabled
      ? "" : "Please select a cancer type.";
    this.setState({ nameError, fileError, cancerTypeError });
    if (nameError || fileError || cancerTypeError) return;
    const { projectionName, selectedHistologicalSubtype, stageVariables, selectedMolecularType, file } = this.state;
    const formData = new FormData();
    formData.append("task_name", projectionName.trim());
    formData.append("histological_subtype", selectedHistologicalSubtype.value);
    formData.append("cancer_type", selectedHistologicalSubtype.cancer_type);
    formData.append("inputVariables", stageVariables);
    formData.append("molecular_type", selectedMolecularType.value);
    formData.append("csvData", file);

    const controller = new AbortController();
    this.submitController = controller;
    this.setState({ submitting: true });
    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_BASE_URL}api/population-projections`,
        formData,
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      this.setState({ submitting: false });
      showSuccessToast("Projection created successfully!");
      this.props.history.push("/app/populationProjection");
    } catch (error) {
      if (!controller.signal.aborted) {
        this.setState({ submitting: false });
        showErrorToast("Unable to create the projection. Please try again.");
      }
    } finally {
      if (this.submitController === controller) this.submitController = null;
    }
  };

  render() {
    const { projectionName, selectedHistologicalSubtype, selectedMolecularType,
      stageVariables, file, fileError, nameError, sampleDownloadUrl, sampleInstructions, sampleLoading, submitting } = this.state;

    return (
      <div className="outer-container new-population-projection">
        <form className="main-container" onSubmit={this.handleCreateAnalysis} noValidate>
          <div className="top-container">
          <Link className="btn-link-primary projection-back" to="/app/populationProjection">
            <img src={backlogo} alt="" /> <span className="ms-2">Back</span>
          </Link>

          <h1 className="form-header">New Population Projection</h1>
          <div className="network-form-container">
            <div className="sections-container">
              <FormGroup className="sections-inner pe-md-4">
                <Label for="projectionName">Projection name<span aria-hidden="true">*</span></Label>
                <Input id="projectionName" placeholder="e.g. Lung cancer miRNA analysis"
                  value={projectionName} invalid={!!nameError} required disabled={submitting}
                  onChange={(event) => this.setState({ projectionName: event.target.value, nameError: "" })}
                  aria-describedby={nameError ? "projection-name-error" : undefined} />
                {nameError && <div id="projection-name-error" className="text-danger small mt-1">{nameError}</div>}
              </FormGroup>
              <FormGroup className="sections-inner">
                <Label for="projectionCancerType">Cancer type / Histological subtype<span aria-hidden="true">*</span></Label>
                <Select inputId="projectionCancerType" options={this.state.cancerTypeOptions} value={selectedHistologicalSubtype}
                  styles={selectStyles} placeholder="Select a cancer type*" aria-required="true"
                  isLoading={this.state.optionsLoading} isDisabled={submitting || this.state.optionsLoading || !!this.state.optionsError}
                  formatOptionLabel={(option) => <span>{option.label}{option.isDisabled ? " (Premium)" : ""}</span>}
                  onChange={(option) => this.setState({ selectedHistologicalSubtype: option, cancerTypeError: "" })} />
                {this.state.cancerTypeError && <div className="text-danger small mt-1">{this.state.cancerTypeError}</div>}
                {this.state.optionsError && <div role="alert" className="text-danger small mt-1">
                  {this.state.optionsError} <Button type="button" color="link" onClick={this.fetchFilterOptions}>Retry</Button>
                </div>}
              </FormGroup>
            </div>
            <hr />
            <div className="projection-analysis-options">
            <fieldset className="projection-molecular-options">
              <legend className="drugType-header">Select Molecular Type<span aria-hidden="true">*</span></legend>
              <div className="d-flex flex-wrap justify-content-start gap-3">
                {molecularOptions.map((option) => <div className="radio-button-container" key={option.value}>
                  <FormGroup check className="mb-0">
                    <Input type="radio" name="molecularType" id={`molecular-${option.value}`} required
                      checked={selectedMolecularType?.value === option.value} disabled={submitting}
                      onChange={() => this.setState({ selectedMolecularType: option })} />
                    <Label check className="radio-label" for={`molecular-${option.value}`}>{option.label}</Label>
                  </FormGroup>
                </div>)}
              </div>
            </fieldset>
              <fieldset className="projection-stage-options">
                <legend className="drugType-header">Stage variables<span aria-hidden="true">*</span></legend>
                {[{ value: "TNM", label: "TNM" }, { value: "TNMG", label: "TNMG (includes grade)" }].map((option) => (
                  <FormGroup check key={option.value} className="mb-2">
                    <Input type="radio" name="stageVariables" id={`stage-${option.value}`} required
                      checked={stageVariables === option.value} disabled={submitting}
                      onChange={() => this.setState({ stageVariables: option.value })} />
                    <Label check for={`stage-${option.value}`} className="radio-label">{option.label}</Label>
                  </FormGroup>
                ))}
                <p className="text-muted small mb-0">Choose the stage variables used to match your data to the population reference.</p>
              </fieldset>
            </div>
            <hr />
            <section aria-labelledby="projection-create-title">
              <h2 id="projection-create-title" className="drugType-header">Upload projection data</h2>
              <p className="text-muted">Upload one molecular data type per analysis.</p>

              <div className="projection-upload-area">
                <Label for="projectionCsv" className="fw-bold">Upload your CSV<span aria-hidden="true">*</span></Label>
                <p className="text-muted small" id="projection-upload-limits">UTF-8 CSV · Up to 2 MiB · 500 patient rows · 300 genes</p>
                <input id="projectionCsv" type="file" ref={this.fileInputRef} accept=".csv,text/csv" required
                  onChange={this.handleFileChange} aria-describedby="projection-upload-limits projection-file-feedback"
                  aria-invalid={!!fileError} className="projection-file-input" disabled={submitting || !!sampleLoading} />
                <div className="projection-upload-actions">
                  <Button type="button" outline color="primary" disabled={submitting || !!sampleLoading}
                    onClick={this.handleSampleFile}>{sampleLoading === "download" ? "Downloading…" : "Download CSV template"}</Button>
                </div>
                {sampleDownloadUrl && (
                  <iframe title="CSV template download" src={sampleDownloadUrl} hidden />
                )}
              </div>
              {sampleInstructions && <p className="text-muted small mt-2 mb-0" role="status">{sampleInstructions}</p>}
              <div id="projection-file-feedback" aria-live="polite" className="small mt-2">
                {fileError ? <span className="text-danger">{fileError}</span> : (
                  <span className="text-muted">{file
                    ? `${file.name} selected.`
                    : "Choose a CSV file to upload for analysis."}</span>
                )}
              </div>

              <details className="projection-csv-format mt-3" open>
                <summary>CSV format</summary>
                <p className="text-muted small mt-2">The first six columns must appear in this exact order, followed by one or more gene columns:</p>
                <pre><code>T_simplified, N_simplified, M_simplified, G_simplified, Overall_survival, Status, GeneA, …</code></pre>
                <p className="text-muted small">Keep the grade column even when using TNM. Survival time is in days; status is 1 for an event and 0 for censored. Enter numeric gene values; missing values may be blank or NA. Each stage group needs at least 3 patient rows for projection.</p>
              </details>

              {/* <div className="projection-included-results">
                <h2 className="drugType-header">Included results</h2>
                <ul className="projection-result-list">
                  {["Prognostic projection", "Treatment projection", "Cox / survival"].map((result) => (
                    <li key={result} className="radio-button-container">{result}</li>
                  ))}
                </ul>
                <p className="text-muted small">All three analyses run together. Treatment and surgery/radiation filters are available on the results page.</p>
              </div> */}

            </section>
          </div>
          </div>
              <div className="bottom-container gap-2">
                <Button tag={Link} to="/app/populationProjection" color="light" className="btn-cancel" disabled={submitting}>Cancel</Button>
                <Button type="submit" color="primary" disabled={!projectionName.trim() || !file || !selectedHistologicalSubtype || this.state.optionsLoading || !!this.state.optionsError || submitting || !!sampleLoading}>
                  {submitting ? "Creating analysis…" : "Create analysis"}
                </Button>
              </div>
        </form>
        {this.state.optionsLoading && (
          <div className="backdrop" role="status" aria-label="Loading population projection options">
            <div className="loader">
              <Loader type="ball-pulse-rise" />
            </div>
          </div>
        )}
        <ToastContainer />
      </div>
    );
  }
}

export default connect((state) => ({ loginSubscriptionDetail: state.counterSlice.loginSubscriptionDetail }))(NewPopulationProjection);
